import React, { useMemo, useState } from "react";
import { css } from "@emotion/css";
import { MathText } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";
import { useGrowthPalette, type PaletteCard } from "../../data/growthPalette";

// ── Styles ──────────────────────────────────────────────────────────

const categoryStyle = css`
  margin: 18px 0 8px;
  font-size: 0.78em;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const boardStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;
`;

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  padding: 13px 15px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease,
    transform 0.06s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: var(--color-bg-raised);
  }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const cardSelectedStyle = css`
  border-color: var(--color-primary) !important;
  background: var(--color-primary-muted) !important;
  box-shadow: 0 0 0 1px var(--color-primary) inset, 0 0 18px var(--color-primary-glow);
`;

const cardTitleStyle = css`
  font-size: 0.93em;
  font-weight: 650;
  line-height: 1.3;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const cardSubStyle = css`
  font-size: 0.8em;
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

const checkStyle = css`
  color: var(--color-primary);
  font-size: 0.9em;
  flex-shrink: 0;
  width: 14px;
`;

const anchorWrapStyle = css`
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border-subtle);
`;

const anchorLabelStyle = css`
  font-size: 0.86em;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
  line-height: 1.45;
`;

const anchorInputStyle = css`
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 11px 13px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.92em;
  box-sizing: border-box;
  &:focus { outline: none; border-color: var(--color-primary); }
  &:disabled { opacity: 0.55; }
  &::placeholder { color: var(--color-text-muted); }
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
`;

const countStyle = css`
  font-size: 0.85em;
  color: var(--color-text-muted);
`;

const continueBtnStyle = css`
  padding: 11px 20px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.95em;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ── Component ───────────────────────────────────────────────────────

export interface GrowthPaletteProps {
  disabled?: boolean;
  /** Card ids + titles tapped, plus the optional free-text anchor. */
  onSubmit: (cardIds: string[], cardTitles: string[], anchorText: string) => void;
}

/**
 * The Resonance Atlas: a categorized board of capability moments the
 * learner taps to express broad PSI, plus an optional anchor text input.
 */
export const GrowthPalette: React.FC<GrowthPaletteProps> = ({ disabled, onSubmit }) => {
  const { catalog, cardsById, loading, error } = useGrowthPalette();
  const [selected, setSelected] = useState<string[]>([]);
  const [anchor, setAnchor] = useState("");

  // Cards grouped under their category, in catalog category order.
  const grouped = useMemo(() => {
    if (!catalog) return [];
    const byCat: Record<string, PaletteCard[]> = {};
    for (const c of catalog.cards) {
      (byCat[c.category] ??= []).push(c);
    }
    return catalog.categories
      .map((cat) => ({ cat, cards: byCat[cat.id] ?? [] }))
      .filter((g) => g.cards.length > 0);
  }, [catalog]);

  const toggle = (card: PaletteCard) => {
    if (disabled) return;
    setSelected((prev) =>
      prev.includes(card.cardId)
        ? prev.filter((x) => x !== card.cardId)
        : [...prev, card.cardId],
    );
  };

  if (loading) {
    return <PendingIndicator variant="block" label="Laying out the atlas…" />;
  }
  if (error) {
    return (
      <div data-testid="growth-palette-error" style={{ color: "var(--color-error)" }}>
        Couldn't load the atlas. Please refresh.
      </div>
    );
  }

  return (
    <div data-testid="growth-palette">
      {grouped.map(({ cat, cards }) => (
        <div key={cat.id}>
          <div className={categoryStyle} data-testid={`atlas-category-${cat.id}`}>
            {cat.name}
          </div>
          <div className={boardStyle}>
            {cards.map((card) => {
              const isSelected = selected.includes(card.cardId);
              return (
                <button
                  key={card.cardId}
                  type="button"
                  className={isSelected ? `${cardStyle} ${cardSelectedStyle}` : cardStyle}
                  onClick={() => toggle(card)}
                  disabled={disabled}
                  aria-pressed={isSelected}
                  data-testid={`palette-card-${card.cardId}`}
                  data-selected={isSelected ? "true" : "false"}
                >
                  <span className={cardTitleStyle}>
                    <span className={checkStyle}>{isSelected ? "✓" : ""}</span>
                    <MathText text={card.title} />
                  </span>
                  <span className={cardSubStyle}>{card.subtitle}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className={anchorWrapStyle}>
        <div className={anchorLabelStyle}>
          {catalog?.anchorPrompt || "Anything specific you want this to help you understand, build, or become?"}
        </div>
        <input
          type="text"
          className={anchorInputStyle}
          placeholder={catalog?.anchorPlaceholder || ""}
          value={anchor}
          disabled={disabled}
          onChange={(e) => setAnchor(e.target.value)}
          data-testid="atlas-anchor-input"
        />
      </div>

      <div className={footerStyle}>
        <div className={countStyle} data-testid="palette-selected-count">
          {selected.length === 0
            ? "Tap what feels alive — mix freely"
            : `${selected.length} selected`}
        </div>
        <button
          type="button"
          className={continueBtnStyle}
          disabled={disabled || selected.length === 0}
          data-testid="palette-continue"
          onClick={() =>
            onSubmit(
              selected,
              selected.map((id) => cardsById[id]?.title ?? id),
              anchor.trim(),
            )
          }
        >
          Continue
        </button>
      </div>
    </div>
  );
};
